import { useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { analyticsConsented, enableAnalytics, enableSmartlook, enableContentsquare, trackPageView } from './common/analytics'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { theme } from './theme'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import Home from './pages/Home'
import LiveTenders from './pages/LiveTenders'
import LiveAwards from './pages/LiveAwards'
import LivePins from './pages/LivePins'
import Pricing from './pages/Pricing'
import Core from './pages/products/Core'
import TenderNinja from './pages/products/TenderNinja'
import Analytics from './pages/products/Analytics'
import BidServices from './pages/products/BidServices'
import NewToGovernment from './pages/solutions/NewToGovernment'
import GrowFasterUKPublicSector from './pages/solutions/GrowFasterUKPublicSector'
import MarketAnalysisFit from './pages/solutions/MarketAnalysisFit'
import ExpandNewSectors from './pages/solutions/ExpandNewSectors'
import GrowOverseas from './pages/solutions/GrowOverseas'
import ImproveEfficiency from './pages/solutions/ImproveEfficiency'
import FindBidPartner from './pages/solutions/FindBidPartner'
import Privacy from './pages/PrivacyPolicy'
import CookiePolicy from './pages/CookiePolicy'
import TermsConditions from './pages/TermsConditions'
import Register from './pages/Register'
import RegisterComplete from './pages/RegisterComplete'
import ContactUs from './pages/ContactUs'
import BookDemo from './pages/BookDemo'
import GlobalOpportunities from './pages/success-network/GlobalOpportunities'
import ProvenApproach from './pages/success-network/ProvenApproach'
import CustomerSuccess from './pages/success-network/CustomerSuccess'
import BusinessWinningNetwork from './pages/success-network/BusinessWinningNetwork'
import CookieBar from './components/CookieBar'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

function AnalyticsTracker() {
  const { pathname } = useLocation()
  const firstLoad = useRef(true)
  useEffect(() => {
    // enableAnalytics sends the initial page_view itself, so only track route changes here
    if (firstLoad.current) {
      firstLoad.current = false
      if (analyticsConsented()) {
        enableAnalytics()
        enableSmartlook()
        enableContentsquare()
      }
      return
    }
    trackPageView(pathname)
  }, [pathname])
  return null
}

// The live-* era (2026-09-24 to 2026-10-09) left those URLs in indexes and bookmarks - route
// them back to the reclaimed legacy URL shapes, mirroring the IIS 301s for client-side nav.
function LiveTendersRedirect() {
  const { keyword } = useParams<{ keyword: string }>()
  return <Navigate to={`/tenderresults/${keyword}`} replace />
}

function LiveAwardsRedirect() {
  const { keyword } = useParams<{ keyword: string }>()
  return <Navigate to={`/awardresults/${keyword}`} replace />
}

function RefCodeCapture() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  useEffect(() => {
    if (code) {
      const safe = code.replace(/[^a-zA-Z0-9_\-]/g, '')
      if (safe) {
        const maxAge = 60 * 60 * 24 * 100
        const isLocal = window.location.hostname === 'localhost'
        const domain = isLocal ? '' : '; Domain=.contractfinderpro.com'
        document.cookie = `refcode=${encodeURIComponent(safe)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${domain}`
      }
    }
    window.location.replace('/register')
  }, [code, navigate])
  // noindex while the redirect runs - referral links are shared widely and must not be indexed
  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  )
}

function AppContent() {
  return (
    <>
      <ScrollToTop />
      <AnalyticsTracker />
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/tenderresults" element={<LiveTenders />} />
        <Route path="/tenderresults/:keyword" element={<LiveTenders />} />
        <Route path="/awardresults" element={<LiveAwards />} />
        <Route path="/awardresults/:keyword" element={<LiveAwards />} />
        <Route path="/live-pins" element={<LivePins />} />
        <Route path="/live-pins/:keyword" element={<LivePins />} />
        <Route path="/live-tenders" element={<Navigate to="/tenderresults" replace />} />
        <Route path="/live-tenders/:keyword" element={<LiveTendersRedirect />} />
        <Route path="/live-awards" element={<Navigate to="/awardresults" replace />} />
        <Route path="/live-awards/:keyword" element={<LiveAwardsRedirect />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/products/core" element={<Core />} />
        <Route path="/products/tender-ninja" element={<TenderNinja />} />
        <Route path="/products/analytics" element={<Analytics />} />
        <Route path="/products/bid-services" element={<BidServices />} />
        <Route path="/solutions/new-to-government" element={<NewToGovernment />} />
        <Route path="/solutions/grow-faster-uk-public-sector" element={<GrowFasterUKPublicSector />} />
        <Route path="/solutions/market-analysis-and-fit" element={<MarketAnalysisFit />} />
        <Route path="/solutions/expand-into-new-sectors" element={<ExpandNewSectors />} />
        <Route path="/solutions/grow-into-europe-and-overseas" element={<GrowOverseas />} />
        <Route path="/solutions/improve-efficiency-and-win-rates" element={<ImproveEfficiency />} />
        <Route path="/solutions/find-a-dedicated-bid-partner" element={<FindBidPartner />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register-complete" element={<RegisterComplete />} />
        <Route path="/contactus" element={<ContactUs />} />
        <Route path="/book-demo" element={<BookDemo />} />
        <Route path="/success-network/global-opportunities" element={<GlobalOpportunities />} />
        <Route path="/success-network/proven-approach" element={<ProvenApproach />} />
        <Route path="/success-network/customer-success" element={<CustomerSuccess />} />
        <Route path="/success-network/business-winning-network" element={<BusinessWinningNetwork />} />
        <Route path="/r/:code" element={<RefCodeCapture />} />
      </Routes>
      <Footer />
      <CookieBar />
    </>
  )
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  )
}
